// AI Generated Component
import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    ViewChild,
    ElementRef,
    OnChanges, SimpleChanges,
} from '@angular/core';
import { PageRepoService } from '@client/app/services/api/page-repo/page-repo.service';
import { HeatmapApiService } from '@client/app/services/entities/heatmaps/heatmaps.service';
import { TotalDomainsService } from '@client/app/services/total-domains/total-domains.service';

@Component({
    selector: 'heatmaps-dialogs-pages',
    standalone: false,
    template: `
        <div class="heatmap-dialogs-pages" *ngIf="show">
            <div class="overlay" (click)="closeDialog()">&nbsp;</div>
            <div class="heatmap-rule-container">
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text" [attr.aria-label]="'Search' | translate">
                            <span class="fas fa-search fa-2x" aria-hidden="true"></span>
                        </span>
                    </div>
                    <label class="sr-only" for="heatmap_rule_pages_search" translate>Search for pages</label>
                    <input type="search"
                           #searchInput
                           [placeholder]="'Search for Pages' | translate"
                           id="heatmap_rule_pages_search"
                           name="search"
                           [(ngModel)]="search"
                           (ngModelChange)="onSearchChange()"
                           class="form-control form-control-lg"
                           autocomplete="off"/>
                </div>
                <div class="pages-container" *ngIf="search.length > pageLengthLimit">
                    <div class="row align-items-stretch max-height">
                        <div *ngIf="loading" class="col-48 text-center align-self-center">
                            <span class="fas fa-spinner fa-spin fa-4x" aria-hidden="true"></span>
                        </div>
                        <div class="col-48 d-flex" *ngIf="!loading">
                            <p class="text-center text-grey font-size-1-75 align-self-center max-width" *ngIf="pages.length === 0" translate>
                                No pages were found
                            </p>
                            <table class="table table-fixed table-hover align-self-stretch max-width" *ngIf="pages.length > 0">
                                <thead>
                                <tr>
                                    <th><div class="sr-only" translate>Url & Title</div></th>
                                    <th class="col-12 col-md-10 col-lg-10"><div class="sr-only" translate>Actions</div></th>
                                </tr>
                                </thead>
                                <tbody>
                                <tr *ngFor="let page of pages">
                                    <td class="text-ellipsis text-v-middle">
                                        <div class="d-flex align-items-center d-flex-ellipsis">
                                            <span class="fas fa-2x fa-copy mr-2 text-link"></span>
                                            <url-title class="py-2"
                                                       [title]="page.title"
                                                       [url]="page.url"
                                                       [active]="false">
                                            </url-title>
                                        </div>
                                        <div class="d-flex align-items-center d-flex-ellipsis ml-5">
                                            <span class="fa-2x fa-globe fas mr-2 text-link"></span>
                                            <url-title [title]="domains[page.id].title"
                                                       [url]="domains[page.id].url"
                                                       [active]="false">
                                            </url-title>
                                        </div>
                                    </td>
                                    <td class="text-right text-v-middle">
                                        <button class="btn btn-border-radius btn-secondary"
                                                type="button"
                                                *ngIf="!hasHeatmapRule(page)"
                                                (click)="selectPage(page)"
                                                translate>
                                            Assign Heatmap Rule
                                        </button>
                                        <span class="text-grey" *ngIf="hasHeatmapRule(page)" translate>
                                        Part of heatmap Rules
                                    </span>
                                    </td>
                                </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
})
export class HeatmapsDialogsPagesComponent implements OnInit, OnChanges {
    @Input() rules: any;
    @Input() selectedRule: any;
    @Input() show: boolean = false;

    @Output() close = new EventEmitter<any>();

    @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement> | null = null;

    pageLengthLimit: number = 0;
    pages: any[] = [];
    loading: boolean = false;
    search: string = '';
    domains: any;
    progress: any;

    private searchTimeout: any;

    constructor (
        private pageRepoService: PageRepoService,
        private heatmapApiService: HeatmapApiService,
        private totalDomainsService: TotalDomainsService,
    ) {}

    ngOnInit (): void {
        this.show = this.show || false;
        this.pageLengthLimit = 3;
        this.pages = [];
        this.loading = true;
        this.search = '';
        this.domains = [];
    }

    ngOnChanges (changes: SimpleChanges) {
        if (changes.show?.currentValue) {
            setTimeout(() => {
                this.searchInput?.nativeElement?.focus();
            });
        }
    }

    onSearchChange (): void {
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
            this.getPage();
        }, 400);
    }

    private getPage (): void {
        if ((typeof this.search !== 'string' || this.search.length === 0) && this.search.length <= this.pageLengthLimit) {
            this.pages = [];
            (this.pages as any).total = 0;
            return;
        }

        const params = {
            page: 1,
            page_size: 100,
            search: this.search,
            type: 'html',
            sort_by: 'url',
            sort_dir: 'asc',
        };

        this.loading = true;

        this.progress = this.pageRepoService.allExistingPages(params)
            .then((pages: any) => {
                const pageIdSet = new Set();
                const uniquePages = pages.filter((page: any) => {
                    if (pageIdSet.has(page.id)) {
                        return false;
                    } else {
                        pageIdSet.add(page.id);
                        return true;
                    }
                });

                this.pages = uniquePages;
                this.mapDomains(uniquePages);
            })
            .catch(() => {
                // Handle error silently
            })
            .finally(() => {
                this.loading = false;
            });
    }

    hasHeatmapRule (page: any): boolean {
        return this.rules.some((rule: any) => {
            return rule.page !== null && rule.page.id === page.id;
        });
    }

    selectPage (page: any): void {
        const params = {
            page_id: page.id,
            heatmap_type: ['scroll', 'movement', 'click'],
        };

        const processCreateRule = () => {
            this.heatmapApiService.create(page.domain_id, params).then((rule: any) => {
                this.closeDialog(rule);
            }).catch(() => {
                // Handle error silently
            });
        };

        if (this.selectedRule) {
            this.heatmapApiService.destroy(this.selectedRule.domain_id, this.selectedRule.id)
                .then(() => {
                    processCreateRule();
                })
                .catch(() => {
                    processCreateRule();
                });
        } else {
            processCreateRule();
        }
    }

    closeDialog (rule?: any): void {
        this.search = '';
        this.show = false;
        this.close.emit({ rule: rule });
    }

    private mapDomains (pages: any[]): void {
        pages.map((page: any) => {
            const target = this.totalDomainsService.domainsEssential?.value?.find((domain: any) => {
                return domain.id === page.domain_id;
            });
            if (target) {
                this.domains[page.id] = target;
            }
        });
    }
}
